/**
 * Created by Carsten Hibbeler on 06.02.2016.
 *
 * Based on FluxContainer by Facebook, Inc.
 */
'use strict';

import FluxStoreGroup from 'flux/lib/FluxStoreGroup';

import invariant from './invariant';
import shallowEqual from './shallowEqual';
import { generateId } from './uid-util';


let DEFAULT_OPTIONS = {
    pure: true,
    withProps: false,
};

/**
 * A FluxContainer is used to subscribe a react component to multiple stores.
 * The stores that are used must be returned from a static `getStores()` method.
 *
 * The component receives information from the stores via state. The state
 * is generated using a static `calculateState()` method that each container
 * must implement. A simple container may look like:
 */

function create(Base, options) {
    enforceInterface(Base);

// Construct the options using default, override with user values as necessary.
    let realOptions = {
        ...DEFAULT_OPTIONS,
        ...(options || {}),
    };

    class FluxContainerClass extends Base {
        // _fluxContainerStoreGroup: FluxStoreGroup;
        // _fluxContainerSubscriptions: Array<{remove: Function}>;

        constructor(props) {
            super(props);
            this.uid = this.uid || generateId(Base.name);
            this.DATA = this.DATA || {};
            this.state = realOptions.withProps
                ? Base.calculateState(this.state, props, this.DATA)
                : Base.calculateState(this.state, undefined, this.DATA);
        }

        withCtx(s) {
            return (this.uid || (this.uid = generateId(Base.name))) + '.' + s;
        }

        componentDidMount() {
            if (super.componentDidMount) {
                super.componentDidMount();
            }

            let stores = Base.getStores();

            // This tracks when any store has changed and we may need to update.
            let changed = false;
            let setChanged = () => {
                changed = true;
            };

            // This adds subscriptions to stores. When a store changes all we do is
            // set changed to true.
            this._fluxContainerSubscriptions = stores.map(
                store => store.addListener(setChanged)
            );

            // This callback is called after the dispatch of the relevant stores. If
            // any have reported a change we update the state, then reset changed.
            let callback = () => {
                if (changed) {
                    this.setState(prevState => {
                        return realOptions.withProps
                            ? Base.calculateState(prevState, this.props, this.DATA)
                            : Base.calculateState(prevState, undefined, this.DATA);
                    });
                }
                changed = false;
            };
            this._fluxContainerStoreGroup = new FluxStoreGroup(stores, callback);
        }

        UNSAFE_componentWillReceiveProps(nextProps, nextContext) {
            if (super.UNSAFE_componentWillReceiveProps) {
                super.UNSAFE_componentWillReceiveProps(nextProps, nextContext);
            }

            // Don't do anything else if the container is not configured to use props.
            if (!realOptions.withProps) {
                return;
            }

            // If it's pure we can potentially optimize out the calculate state.
            if (realOptions.pure && shallowEqual(this.props, nextProps)) {
                return;
            }

            // Finally update the state using the new props.
            this.setState(prevState => Base.calculateState(prevState, nextProps));
        }

        componentWillUnmount() {
            if (super.componentWillUnmount) {
                super.componentWillUnmount();
            }

            this._fluxContainerStoreGroup.release();
            for (var subscription of this._fluxContainerSubscriptions) {
                subscription.remove();
            }
            this._fluxContainerSubscriptions = [];
        }
    }

// Make sure we override shouldComponentUpdate only if the pure option is
// specified. We can't override this above because we don't want to override
// the default behavior on accident. Super works weird with react ES6 classes
// right now.
    let container = realOptions.pure ? createPureContainer(FluxContainerClass) : (FluxContainerClass);

// Update the name of the container before returning.
    let componentName = Base.displayName || Base.name;
    container.displayName = 'FluxContainer(' + componentName + ')';

    return container;
}

// TODO: typecheck this better.
function createPureContainer(FluxContainerBase) {
    class PureFluxContainerClass extends FluxContainerBase {
        shouldComponentUpdate(nextProps, nextState) {
            return (
                !shallowEqual(this.props, nextProps) || !shallowEqual(this.state, nextState)
            );
        }
    }

    return PureFluxContainerClass;
}

function enforceInterface(o) {
    invariant(
        o.getStores,
        'Components that use FluxContainer must implement `static getStores()`'
    );
    invariant(
        o.calculateState,
        'Components that use FluxContainer must implement `static calculateState()`'
    );
}

export default { create };



// WEBPACK FOOTER //
// ./js/utils/ContextContainer.js